import { FC, useState } from "react";
import { useFormik } from "formik";
import { z } from "zod";
import { toFormikValidate } from "zod-formik-adapter";
import { Input } from "@/components/UI";
import PhoneInput from "@/components/UI/PhoneInput";
import { isPhoneValid } from "@/utils/formValidator";
import { TextArea } from "@/components/UI/Input";
import ActionButton from "@/components/UI/Buttons/ActionButton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/UI/Dialog/Dialog";
import { toast } from "sonner";

const contactValidationSchema = z
  .object({
    firstName: z.string().min(1, "Vardas yra privalomas"),
    lastName: z.string().min(1, "Pavardė yra privaloma"),
    email: z
      .string()
      .email("Neteisingas el. pašto adresas")
      .min(1, "El. paštas yra privalomas"),
    phone: z
      .string()
      .min(1, "Tel. numeris yra privalomas")
      .refine((v) => isPhoneValid(v), "Neteisingas telefono numerio formatas"),
    phoneCountry: z.string().optional(),
    message: z.string().min(1, "Žinutė yra privaloma"),
  })
  .refine((data) => isPhoneValid(data.phone, data.phoneCountry), {
    message: "Telefono numeris neatitinka pasirinktos šalies",
    path: ["phone"],
  });

type FormValues = z.infer<typeof contactValidationSchema>;

interface ContactModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const ContactModal: FC<ContactModalProps> = ({ isOpen, onOpenChange }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik<FormValues>({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      phoneCountry: "LT",
      message: "",
    },
    validate: toFormikValidate(contactValidationSchema),
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));
        console.log("Contact form submitted: ", values);

        toast.success("Žinutė sėkmingai išsiųsta!");
        resetForm();
        onOpenChange(false);
      } catch {
        toast.error("Įvyko klaida siunčiant žinutę. Bandykite dar kartą.");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      formik.resetForm();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="h-full w-full max-w-full border-0 bg-white sm:h-auto sm:w-auto sm:max-w-[600px] sm:rounded-lg sm:border">
        <div className="h-full max-h-screen overflow-x-hidden overflow-y-auto p-8 sm:max-h-[85dvh]">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-dark text-xl font-medium sm:text-2xl">
              Pasidalinkite, kaip galime padėti
            </DialogTitle>
          </DialogHeader>

          <form
            onSubmit={formik.handleSubmit}
            className="flex w-full flex-col gap-5 sm:gap-6"
          >
            <div className="flex w-full flex-col gap-5 sm:flex-row sm:gap-4">
              <div className="flex w-full flex-col">
                <Input
                  id="firstName"
                  name="firstName"
                  label="Vardas*"
                  placeholder="Jūsų vardas"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variant="dark"
                />
              </div>

              <div className="flex w-full flex-col">
                <Input
                  id="lastName"
                  name="lastName"
                  label="Pavardė*"
                  placeholder="Jūsų pavardė"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variant="dark"
                />
              </div>
            </div>

            <div className="flex w-full flex-col">
              <Input
                id="email"
                name="email"
                type="email"
                label="El. paštas*"
                placeholder="Jūsų el. paštas"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                variant="dark"
              />
            </div>

            <div className="flex w-full flex-col">
              <PhoneInput
                id="phone"
                name="phone"
                label="Tel. numeris*"
                value={formik.values.phone}
                onChange={(val) => formik.setFieldValue("phone", val)}
                onCountryChange={(c) => formik.setFieldValue("phoneCountry", c ?? "LT")}
                onBlur={formik.handleBlur}
                variant="dark"
                error={
                  formik.touched.phone && formik.errors.phone
                    ? String(formik.errors.phone)
                    : undefined
                }
              />
            </div>

            <div className="flex w-full flex-col">
              <TextArea
                id="message"
                name="message"
                label="Žinutė*"
                placeholder="Praneškite mums, kuo galime padėti — dėl narysčių, registracijos keliems asmenims ar bendrų klausimų."
                value={formik.values.message}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full flex-col items-start gap-0.5"
                inputClassName="min-h-[120px] resize-y"
                variant="dark"
              />
            </div>

            <div className="mt-2 flex w-full flex-col gap-3">
              {Object.keys(formik.errors).length > 0 &&
                formik.submitCount > 0 && (
                  <div className="text-center text-sm text-red-500">
                    Prašau užpildyti visus privalomus laukus
                  </div>
                )}
              <ActionButton
                type="submit"
                disabled={isSubmitting}
                variant="dark"
                showIcon={false}
                className="w-full justify-center"
              >
                {isSubmitting ? "Siunčiama..." : "Siųsti žinutę"}
              </ActionButton>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
